/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * One canonical risk category and how findings are classified into it.
 */
export type RiskCategoryDefinition = {
  /**
   * Plain-English description of what this category covers.
   */
  description: string;
  /**
   * Lucide icon name suggested for this category.
   */
  icon: string;
  /**
   * Canonical category key (e.g. 'secrets', 'pii', 'shadow_mcp').
   */
  key: string;
  /**
   * Human-readable category label for UI rendering.
   */
  label: string;
  /**
   * False when the category is session-scoped and message scoping does not apply (e.g. account_identity).
   */
  recommendedScopeApplicable: boolean;
  /**
   * Centrally recommended CEL exemption predicate; empty = no exemption.
   */
  recommendedScopeExempt: string;
  /**
   * Centrally recommended CEL scope predicate for this category; empty = no include restriction.
   */
  recommendedScopeInclude: string;
  /**
   * User-facing explanation of the recommended scope and the consequences of disabling it. Empty when the category has no recommendation.
   */
  recommendedScopeRationale: string;
  /**
   * When non-empty, findings whose rule_id starts with this prefix belong to this category. The catch-all for a family (e.g. 'pii.').
   */
  ruleIdPrefix: string;
  /**
   * When non-empty, findings whose rule_id is in this exact list belong to this category. Checked before rule_id_prefix.
   */
  ruleIds: Array<string>;
  /**
   * When non-empty, findings whose source equals this value belong to this category.
   */
  source: string;
};

/** @internal */
export const RiskCategoryDefinition$inboundSchema: z.ZodMiniType<
  RiskCategoryDefinition,
  unknown
> = z.pipe(
  z.object({
    description: z.string(),
    icon: z.string(),
    key: z.string(),
    label: z.string(),
    recommended_scope_applicable: z.boolean(),
    recommended_scope_exempt: z.string(),
    recommended_scope_include: z.string(),
    recommended_scope_rationale: z.string(),
    rule_id_prefix: z.string(),
    rule_ids: z.array(z.string()),
    source: z.string(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "recommended_scope_applicable": "recommendedScopeApplicable",
      "recommended_scope_exempt": "recommendedScopeExempt",
      "recommended_scope_include": "recommendedScopeInclude",
      "recommended_scope_rationale": "recommendedScopeRationale",
      "rule_id_prefix": "ruleIdPrefix",
      "rule_ids": "ruleIds",
    });
  }),
);

export function riskCategoryDefinitionFromJSON(
  jsonString: string,
): SafeParseResult<RiskCategoryDefinition, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => RiskCategoryDefinition$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'RiskCategoryDefinition' from JSON`,
  );
}
